import memoize from "@-/com.npmjs.memoize";
import {
  type CurriculumDataset,
  type CurriculumGradeParts,
  type Level,
  formatCurriculumGradeLabel,
  getCurriculumMaterialBands,
  levelMatchesCurriculumMaterialTab,
} from "@-/dev.improve.haniverse.lib.level";

/** Learned characters of one curriculum grade the pack covers ("HSK 3", "Tier 1", "MOE B"). */
export interface LevelsPackGradeProgress {
  grade: number;
  label: string;
  learned: number;
  total: number;
}

export interface LevelsPackProgress {
  /** Distinct characters across the pack's levels, each counted once. */
  characterCount: number;
  completedLevelCount: number;
  grades: readonly LevelsPackGradeProgress[];
  /** How many of `characterCount` the learner has studied. */
  learnedCharacterCount: number;
  levelCount: number;
  /** The pack's first incomplete level (its curriculum-wide index), or null once all are done. */
  nextLevelIndex: number | null;
}

/**
 * Every character of each curriculum grade, wherever the curriculum teaches it: an HSK 3 character
 * taught early as another character's component still counts toward HSK 3.
 */
const getCharactersByGrade = memoize(
  (levels: readonly Level[]): ReadonlyMap<number, readonly string[]> => {
    const byGrade = new Map<number, Set<string>>();
    for (const level of levels) {
      for (const [position, character] of level.characters.entries()) {
        const grade = level.characterGrades?.[position];
        if (grade === undefined || grade === null) {
          continue;
        }
        const characters = byGrade.get(grade) ?? new Set();
        characters.add(character);
        byGrade.set(grade, characters);
      }
    }
    return new Map(Array.from(byGrade, ([grade, characters]) => [grade, [...characters]]));
  },
);

/**
 * How far the learner is through a Browse → Levels pack: its character levels completed (every
 * character studied), the first one still incomplete, and, per curriculum grade the pack covers,
 * how many of that grade's characters the learner has studied. A split grade (Tier 2 over Xplorer
 * and Yonder) reports the whole grade in each part. The pack's own characters studied give its
 * overall progress.
 */
export function getLevelsPackProgress({
  dataset,
  gradeParts,
  levels,
  studied,
  tabValue,
}: {
  dataset: CurriculumDataset;
  gradeParts: CurriculumGradeParts;
  levels: readonly Level[];
  studied: ReadonlySet<string>;
  tabValue: number;
}): LevelsPackProgress {
  let completedLevelCount = 0;
  let levelCount = 0;
  let nextLevelIndex: number | null = null;
  const packCharacters = new Set<string>();
  for (const level of levels) {
    if (
      level.characters.length === 0 ||
      !levelMatchesCurriculumMaterialTab(level, tabValue, { dataset, gradeParts })
    ) {
      continue;
    }
    levelCount++;
    for (const character of level.characters) {
      packCharacters.add(character);
    }
    if (level.characters.every((character) => studied.has(character))) {
      completedLevelCount++;
    } else {
      nextLevelIndex ??= level.index;
    }
  }
  const band = getCurriculumMaterialBands(dataset).find((candidate) => candidate.id === tabValue);
  const charactersByGrade = getCharactersByGrade(levels);
  const grades = (band?.grades ?? []).flatMap((grade): LevelsPackGradeProgress[] => {
    const characters = charactersByGrade.get(grade);
    if (characters === undefined) {
      return [];
    }
    return [
      {
        grade,
        label: formatCurriculumGradeLabel(grade, { dataset }),
        learned: characters.filter((character) => studied.has(character)).length,
        total: characters.length,
      },
    ];
  });
  return {
    characterCount: packCharacters.size,
    completedLevelCount,
    grades,
    learnedCharacterCount: [...packCharacters].filter((character) => studied.has(character)).length,
    levelCount,
    nextLevelIndex,
  };
}
